import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { detachJob, dismissLostJob, selectJobByCourse, selectJobLost } from './jobsSlice'
import { useGetStudioJobQuery } from './studioApi'
import { isTerminalStatus, pendingGateOf } from './studioTypes'
import { readProgress } from './lib/jobProgress'

/**
 * Единственный интерфейс к АКТИВНОМУ (уже привязанному) прогону курса для
 * C-G: опрос статуса, готовый прогресс, `detach()`.
 *
 * Опрос ведёт JobPoller; здесь `pollingInterval` НЕ задаётся намеренно —
 * RTK Query разделяет один кеш между подписчиками, и второй интервал только
 * учащал бы запросы.
 *
 * **Привязка (`attach`) — НЕ через этот хук.** У него `attach`-метода нет: с
 * M-5 (Task 8, ревью раунд 1) перецепление реестра на другой прогон делается
 * прямым `dispatch(attachJob({ courseId, entry }))` (см. `jobsSlice.ts`) в
 * месте, которое решает, ЧЕМ прогон запущен (`useStartGeneration`) или НА
 * КАКОЙ чужой прогон переключиться (`useCourseBusyGate.showRun`) — у
 * `useCourseJob` этого контекста нет и плодить лишний параметр под каждый
 * вызывающий незачем.
 *
 * **Владение очисткой реестра.** Терминальную запись (`success`/`failed`/`cancelled`)
 * никто не удаляет автоматически: поллер намеренно её не трогает — иначе
 * `useCourseJob` потерял бы терминальный ответ (summary, credits_charged)
 * ровно в момент, когда экран должен его показать. Значит чистит
 * ПОТРЕБИТЕЛЬ: вызови `detach()` СРАЗУ ПОСЛЕ того, как показал итог прогона
 * автору (тост/баннер). Без этого запись остаётся в localStorage навсегда и
 * при каждой загрузке приложения тянет за собой лишний запрос.
 */
export const useCourseJob = (courseId: number) => {
  const dispatch = useAppDispatch()
  const entry = useAppSelector((s) => selectJobByCourse(s, courseId))
  const isLost = useAppSelector((s) => selectJobLost(s, courseId))
  const { data } = useGetStudioJobQuery(entry?.jobId ?? 0, { skip: !entry })

  const detach = useCallback(() => dispatch(detachJob(courseId)), [dispatch, courseId])
  const dismissLost = useCallback(() => dispatch(dismissLostJob(courseId)), [dispatch, courseId])

  // Потребитель не обязан сам соединять запись реестра (fallback-тотал со
  // старта) и джобу (`*_total`, когда он уже пришёл) — готовый прогресс.
  const progress = data
    ? readProgress(data, entry?.total ?? null)
    : { done: 0, total: entry?.total ?? null }

  // Гейт на `entry`, а не `data ?? null` напрямую: RTK Query держит
  // последний успешный `data` даже когда `skip` становится `true` (нашли
  // на I-2 раунда 2 — после `detach()` итог продолжал маячить в DOM, хотя
  // реестр уже был пуст). Без гейта `job` соврал бы про прогон, который
  // автор только что закрыл.
  const job = entry ? (data ?? null) : null
  const pendingGate = pendingGateOf(job)

  return {
    job,
    entry: entry ?? null,
    isPolling: Boolean(entry) && !isTerminalStatus(data?.status),
    /** Пауза считается по СТАТУСУ и наличию открытого вопроса сразу: статус
     *  мог обновиться раньше списка гейтов и наоборот, а показывать «ждут
     *  вас» без вопроса на экране — врать. */
    isAwaitingAuthor: job?.status === 'awaiting_author' && pendingGate !== null,
    /**
     * Запись в реестре есть, а первого ответа ещё нет. `isPolling` в этот
     * момент тоже true (и блокировать ввод правильно — статус может
     * оказаться любым), но ЧИСЛА показывать нельзя: запись могла пролежать в
     * persist неделю, и «Генерация курса: 0 из 5» было бы прямой ложью.
     */
    isStatusUnknown: Boolean(entry) && data === undefined,
    progress,
    /**
     * Прогон исчез на бэке (404/403) — запись и история уже стёрты поллером.
     * Потребитель обязан сказать об этом автору: иначе лента и его реплика
     * пропадают молча (M-6).
     */
    isLost,
    dismissLost,
    detach,
  }
}
